import { useContext, useMemo } from 'react';
import Link from 'fe-design-base/molecules/Link';

import { toI18n } from 'util/i18n';

import { ROUTE_I18N_MAPPING } from './constants';
import { LastPageContext } from './LastPageContext';

export interface BackLinkProps {
  size?: string;
  dataTestId?: string;
  fallbackLinkTo: string;
  uxElement?: string;
  queryParams?: Record<string, string>;
  onClick?: (...args: any[]) => void;
}

const BackLink = ({
  size = 'medium',
  dataTestId,
  uxElement = 'link',
  fallbackLinkTo,
  queryParams,
  onClick,
}: BackLinkProps) => {
  const pageContext = useContext(LastPageContext);

  const linkTo = useMemo((): string => {
    if (pageContext && pageContext.previousPathname) {
      return pageContext.previousPathname;
    }

    return fallbackLinkTo;
  }, [pageContext, fallbackLinkTo]);

  const linkTextContent = useMemo((): string => {
    const lastRouteI18n = toI18n(
      `fe_design_base.back_link.routes.${ROUTE_I18N_MAPPING[linkTo]}`
    );

    if (!lastRouteI18n) {
      return toI18n('fe_design_base.back_link.back');
    }

    return `${toI18n('fe_design_base.back_link.back_to')} ${lastRouteI18n}`;
  }, [linkTo]);

  const linkToWithQueryParams = useMemo((): string => {
    if (queryParams) {
      return `${linkTo}?${new URLSearchParams(queryParams).toString()}`;
    }
    return linkTo;
  }, [linkTo, queryParams]);

  return (
    <Link
      startIcon="ArrowLeft"
      dataTestId={dataTestId}
      size={size}
      linkTo={linkToWithQueryParams}
      uxElement={uxElement}
      onClick={onClick}
    >
      {linkTextContent}
    </Link>
  );
};

export default BackLink;
